import { Locales } from 'shared/assets/localization';
import { NLP } from 'shared/constants';
import { DateFormatOptions } from 'shared/types/date-format-options';

/**
 * Calculates and formats the due date based on a keyword.
 * This is the core of the natural language date parsing.
 */
export const getDisplayDate = (
  keyword: string,
  language: Locales,
  dateFormat: DateFormatOptions,
  isDeadline = false,
) => {
  if (!keyword || !keyword.trim()) {
    return null;
  }
  const now = new Date();
  // Use start of day for comparisons to avoid time-related issues.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let date = new Date();

  const dayIndexMap: Record<Locales, Record<string, number>> = {
    [Locales.EnUS]: { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 },
    [Locales.NlNL]: { zo: 0, ma: 1, di: 2, wo: 3, do: 4, vr: 5, za: 6 },
    [Locales.EsES]: {
      dom: 0,
      domingo: 0,
      lun: 1,
      lunes: 1,
      mar: 2,
      martes: 2,
      mié: 3,
      miércoles: 3,
      jue: 4,
      jueves: 4,
      vie: 5,
      viernes: 5,
      sáb: 6,
      sábado: 6,
    },
    [Locales.DeDE]: { so: 0, mo: 1, di: 2, mi: 3, do: 4, fr: 5, sa: 6 },
  };
  const currentDayIndexMap = dayIndexMap[language];
  const currentMonthsMap = NLP.MONTHS[language];
  const lowerKeyword = keyword.toLowerCase().trim();
  const parts = lowerKeyword.split(/\s+/);
  const todayDay = now.getDay();

  const k = {
    [Locales.EnUS]: {
      today: ['today', 'tod'],
      tomorrow: ['tomorrow', 'tmw', 'tmr'],
      next_week: ['next week'],
      this_weekend: ['this weekend'],
      next_weekend: ['next weekend'],
      next_year: ['next year'],
      next_month: ['next month'],
      end_of_month: ['end of month'],
    },
    [Locales.NlNL]: {
      today: ['vandaag'],
      tomorrow: ['morgen'],
      next_week: ['volgende week'],
      this_weekend: ['dit weekend'],
      next_weekend: ['volgend weekend'],
      next_year: ['volgend jaar'],
      next_month: ['volgende maand'],
      end_of_month: ['eind van de maand'],
    },
    [Locales.EsES]: {
      today: ['hoy'],
      tomorrow: ['mañana'],
      next_week: [
        'la proxima semana',
        'la próxima semana',
        'proxima semana',
        'próxima semana',
      ],
      this_weekend: ['este fin de semana'],
      next_weekend: [
        'el proximo fin de semana',
        'el próximo fin de semana',
        'proximo fin de semana',
        'próximo fin de semana',
      ],
      next_year: [
        'el proximo año',
        'el próximo año',
        'proximo año',
        'próximo año',
      ],
      next_month: [
        'el proximo mes',
        'el próximo mes',
        'proximo mes',
        'próximo mes',
      ],
      end_of_month: ['fin de mes'],
    },
    [Locales.DeDE]: {
      today: ['heute'],
      tomorrow: ['morgen'],
      next_week: ['nächste woche', 'naechste woche'],
      this_weekend: ['dieses wochenende'],
      next_weekend: ['nächstes wochenende', 'naechstes wochenende'],
      next_year: ['nächstes jahr', 'naechstes jahr'],
      next_month: ['nächsten monat', 'naechsten monat'],
      end_of_month: ['ende des monats'],
    },
  }[language];

  // --- Start of Corrected Logic for Spanish Phrases ---
  const dayStrForProximo = parts[parts.length - 1];
  const isDayForProximo = Object.keys(currentDayIndexMap).some((d) =>
    dayStrForProximo.startsWith(d),
  );
  const isProximo =
    language === Locales.EsES &&
    /^(el\s+)?(próximo|proxima)/.test(lowerKeyword) &&
    isDayForProximo;
  const isQueViene =
    language === Locales.EsES && /que\s+viene$/.test(lowerKeyword);

  if (isProximo) {
    const dayStr = parts[parts.length - 1]; // "viernes" is the last word
    const targetDayKey = Object.keys(currentDayIndexMap).find((d) =>
      dayStr.startsWith(d),
    );
    if (targetDayKey) {
      const targetDay = currentDayIndexMap[targetDayKey];
      const diff = (targetDay + 7 - todayDay) % 7;
      date.setDate(now.getDate() + diff + 7); // "próximo" always means the one in the following week
    } else {
      return null;
    }
  } else if (isQueViene) {
    const dayStr = parts[0] === 'el' ? parts[1] : parts[0]; // "viernes" is the first or second word
    const targetDayKey = Object.keys(currentDayIndexMap).find((d) =>
      dayStr.startsWith(d),
    );
    if (targetDayKey) {
      const targetDay = currentDayIndexMap[targetDayKey];
      const diff = (targetDay + 7 - todayDay) % 7;
      date.setDate(now.getDate() + (diff === 0 ? 7 : diff)); // "que viene" means the soonest upcoming one
    } else {
      return null;
    }
  } else if (
    (language === Locales.EnUS && lowerKeyword.startsWith('next ')) ||
    (language === Locales.NlNL && lowerKeyword.startsWith('volgende ')) ||
    (language === Locales.DeDE &&
      (lowerKeyword.startsWith('nächsten ') ||
        lowerKeyword.startsWith('naechsten ')))
  ) {
    const dayStr = parts[parts.length - 1];
    const targetDayKey = Object.keys(currentDayIndexMap).find((d) =>
      dayStr.startsWith(d),
    );
    if (targetDayKey) {
      const targetDay = currentDayIndexMap[targetDayKey];
      const diff = (targetDay + 7 - todayDay) % 7;
      date.setDate(now.getDate() + diff + 7); // "next" always means the one in the following week
    } else {
      return null;
    }
  }

  // --- End of Corrected Logic ---
  else if (k.today.includes(lowerKeyword)) {
    // Stays today
  } else if (k.tomorrow.includes(lowerKeyword)) {
    date.setDate(now.getDate() + 1);
  } else if (k.next_week.includes(lowerKeyword)) {
    if (isDeadline) {
      const daysUntilNextFriday = ((5 - todayDay + 7) % 7) + 7;
      date.setDate(now.getDate() + daysUntilNextFriday);
    } else {
      date.setDate(now.getDate() - todayDay + 1 + 7); // Next Monday
    }
  } else if (k.this_weekend.includes(lowerKeyword)) {
    const diff = (6 + 7 - todayDay) % 7; // Get to Saturday
    date.setDate(now.getDate() + diff);
  } else if (k.next_weekend.includes(lowerKeyword)) {
    const diff = (6 + 7 - todayDay) % 7; // Get to this Saturday, then add 7 days
    date.setDate(now.getDate() + diff + 7);
  } else if (k.next_year.includes(lowerKeyword)) {
    date = new Date(now.getFullYear() + 1, 0, 1);
  } else if (k.next_month.includes(lowerKeyword)) {
    date.setMonth(now.getMonth() + 1, 1); // Set to the 1st of next month
  } else if (k.end_of_month.includes(lowerKeyword)) {
    date = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
  } else if (
    parts.length === 3 &&
    currentMonthsMap.hasOwnProperty(parts[0]) &&
    !isNaN(parseInt(parts[1])) &&
    /^\d{4}$/.test(parts[2])
  ) {
    // "feb 9 2026"
    const day = parseInt(parts[1], 10);
    const monthIndex = currentMonthsMap[parts[0]];
    const year = parseInt(parts[2], 10);
    if (monthIndex !== undefined) {
      const tempDate = new Date(year, monthIndex, day);
      if (
        tempDate.getFullYear() === year &&
        tempDate.getMonth() === monthIndex &&
        tempDate.getDate() === day
      ) {
        date = tempDate;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } else if (
    parts.length === 3 &&
    !isNaN(parseInt(parts[0])) &&
    currentMonthsMap.hasOwnProperty(parts[1]) &&
    /^\d{4}$/.test(parts[2])
  ) {
    // "9 feb 2026"
    const day = parseInt(parts[0], 10);
    const monthIndex = currentMonthsMap[parts[1]];
    const year = parseInt(parts[2], 10);
    if (monthIndex !== undefined) {
      const tempDate = new Date(year, monthIndex, day);
      if (
        tempDate.getFullYear() === year &&
        tempDate.getMonth() === monthIndex &&
        tempDate.getDate() === day
      ) {
        date = tempDate;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } else if (
    parts.length === 2 &&
    !isNaN(parseInt(parts[0])) &&
    currentMonthsMap.hasOwnProperty(parts[1])
  ) {
    // "28 jun"
    const day = parseInt(parts[0], 10);
    const monthIndex = currentMonthsMap[parts[1]];
    if (monthIndex !== undefined) {
      let tempDate = new Date(now.getFullYear(), monthIndex, day);
      if (tempDate.getMonth() !== monthIndex || tempDate.getDate() !== day) {
        return null;
      }
      if (tempDate < today) {
        tempDate.setFullYear(now.getFullYear() + 1);
      }
      date = tempDate;
    } else {
      return null;
    }
  } else if (
    parts.length === 2 &&
    currentMonthsMap.hasOwnProperty(parts[0]) &&
    !isNaN(parseInt(parts[1]))
  ) {
    // "jun 28"
    const day = parseInt(parts[1], 10);
    const monthIndex = currentMonthsMap[parts[0]];
    if (monthIndex !== undefined) {
      let tempDate = new Date(now.getFullYear(), monthIndex, day);
      if (tempDate.getMonth() !== monthIndex || tempDate.getDate() !== day) {
        return null;
      }
      if (tempDate < today) {
        tempDate.setFullYear(now.getFullYear() + 1);
      }
      date = tempDate;
    } else {
      return null;
    }
  } else if (
    /^\d{1,2}(st|nd|rd|th|e|ste|º|ª|er|\.)$/.test(lowerKeyword) ||
    (language === Locales.EsES && /^el\s\d{1,2}$/.test(lowerKeyword))
  ) {
    const day = parseInt(lowerKeyword.replace('el ', ''), 10);
    let tempDate = new Date(now.getFullYear(), now.getMonth(), day);
    if (tempDate.getMonth() !== now.getMonth() || tempDate.getDate() !== day) {
      return null;
    }
    if (tempDate < today) {
      tempDate.setMonth(now.getMonth() + 1);
    }
    date = tempDate;
  } else if (/^\d{1,2}[\.\/]\d{1,2}(?:[\.\/]\d{2,4})?$/.test(lowerKeyword)) {
    const separator = lowerKeyword.includes('/') ? '/' : '.';
    const dateParts = lowerKeyword.split(separator);

    let day, month;

    // Based on the dateFormat setting, interpret the first two parts as either day/month or month/day.
    if (dateFormat === DateFormatOptions.DayMonthYear) {
      day = parseInt(dateParts[0], 10);
      month = parseInt(dateParts[1], 10) - 1;
    } else {
      month = parseInt(dateParts[0], 10) - 1;
      day = parseInt(dateParts[1], 10);
    }
    let year =
      dateParts.length === 3
        ? dateParts[2].length === 2
          ? 2000 + parseInt(dateParts[2], 10)
          : parseInt(dateParts[2], 10)
        : now.getFullYear();
    if (new Date(year, month, day) < today && dateParts.length < 3) {
      year++;
    }
    const tempDate = new Date(year, month, day);
    if (
      tempDate.getFullYear() !== year ||
      tempDate.getMonth() !== month ||
      tempDate.getDate() !== day
    ) {
      return null;
    }
    date = tempDate;
  } else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(lowerKeyword)) {
    const tempDate = new Date(lowerKeyword);
    if (isNaN(tempDate.getTime())) {
      return null;
    }
    date = new Date(tempDate.getTime() + tempDate.getTimezoneOffset() * 60000);
  } else {
    const targetDayKey = Object.keys(currentDayIndexMap).find((d) =>
      lowerKeyword.startsWith(d),
    );
    if (targetDayKey) {
      const targetDay = currentDayIndexMap[targetDayKey];
      const diff = (targetDay + 7 - now.getDay()) % 7;
      date.setDate(now.getDate() + (diff === 0 ? 7 : diff));
    } else {
      return null;
    }
  }

  return {
    date,
    dateIsoString: date.toISOString(),
  };
};
